import React, { Component } from "react";
import { Form, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { PiEye, PiInfo } from "react-icons/pi";
import { toast } from "react-toastify";
import axios from "../../../axios";
import axiosLib from "axios";
import apiError from "../../../error";
import { getDate } from "../../../function";
import simulationandtrain from "../../../assets/create_campaign/simulationandtrain.png";
import "./steps.css";
import { attackVectorUsesLandingPage } from "../../ai-components/campaign-template-utils";

const getCancelSource = () => {
	const CancelToken = axiosLib.CancelToken;
	return CancelToken ? CancelToken.source() : null;
};

// Templates are loaded 10 at a time and appended as the user scrolls (infinite scroll) to keep
// the payload / server load small instead of fetching every template at once.
const TEMPLATE_PAGE_SIZE = 10;

/** Normalize API/parent values; avoids !!\"false\" === true. */
const coerceCredentialHarvesting = (raw) => {
	if (raw === true || raw === false) return raw;
	if (raw === 1 || raw === 0) return raw === 1;
	if (typeof raw === "string") {
		const s = String(raw).trim().toLowerCase();
		if (s === "true" || s === "1" || s === "yes" || s === "on") return true;
		if (s === "false" || s === "0" || s === "no" || s === "off" || s === "") return false;
	}
	return undefined;
};

const pickCredentialHarvestingRaw = (obj) => {
	if (!obj || typeof obj !== "object") return undefined;
	return obj.credential_harvesting ?? obj.credentialHarvesting;
}

/* A landing page is optional. `page.landing_url` is the flag; the backend normalises
   "none" to NULL on write, so truthiness is the whole test.
   Returns undefined when the endpoint doesn't report a page at all (system templates),
   so callers can tell "no landing page" apart from "don't know". */
const readLandingUrl = (t) => {
	if (!t || !Object.prototype.hasOwnProperty.call(t, 'page')) return undefined;
	return t.page?.landing_url || null;
};;

/** Prefer full multi-select ids from parent; fallback to single selectedCategory (legacy). */
function categoryIdsFromProps(props) {
	if (Array.isArray(props.selectedCategoryIds) && props.selectedCategoryIds.length > 0) {
		return [...props.selectedCategoryIds];
	}
	if (props.selectedCategory != null && props.selectedCategory !== "") {
		return [props.selectedCategory];
	}
	return [];
}

export default class TemplateSelectionStep extends Component {
	constructor(props) {
		super(props);
		this.categoriesCancel = null;
		this.templatesCancel = null;
		this.simulationCancel = null;
		this.templatesObserver = null;
		const initialCatIds = categoryIdsFromProps(props);
		this.state = {
			loading: true,
			categories: [],
			selectedCategory: initialCatIds[0] || "",
			selectedCategoryIds: initialCatIds,
			// Distinguishes "no filter / show everything" (selectedCategoryIds: [] by default) from the user
			// explicitly unchecking every category (selectedCategoryIds: [] too, but should show zero templates).
			categoryFilterNone: false,
			templates: [],
 			templatePath: "",
			templateMode: props.templateMode || "single",
			selectedTemplateId: props.templateId || null,
			selectedTemplateIds: Array.isArray(props.selectedTemplateIds) ? props.selectedTemplateIds : [],
			templateSearchInput: "",
			// Infinite scroll paging state.
			page: 1,
			hasMore: false,
			loadingMore: false,
			totalCount: 0,
			// System Templates must be cloned before use; "My Templates" run directly.
			templateType: props.templateType === "System Templates" ? "System Templates" : "My Templates",
			// Attack-type id is required to filter system (store) templates by the chosen attack vector.
			storeAttackTypeId: null,
			// Fetched from simulation when props are empty (follow-up flow)
			fetchedAttackType: "",
			fetchedSimulationType: "",
			fetchedCredentialHarvesting: false,
			fetchedTargetInteractionType: "",
			fetchedIsAIbased: false,
		};
	}

	isSystemType = () => this.state.templateType === "System Templates";

	getAttackType = () => this.props.attackType || this.state.fetchedAttackType;
	getSimulationType = () => this.props.simulationType || this.state.fetchedSimulationType;
	getCredentialHarvesting = () => {
		const cd = this.props.campaignDetails?.credentialHarvesting;
		if (cd !== undefined && cd !== null) {
			return coerceCredentialHarvesting(cd) ?? false;
		}
		const p = this.props.credentialHarvesting;
		if (p !== undefined && p !== null) {
			return coerceCredentialHarvesting(p) ?? false;
		}
		return coerceCredentialHarvesting(this.state.fetchedCredentialHarvesting) ?? false;
	};
	/** Prefer campaign details when set; blank string is treated as unset. For mode-based vectors, derive from isAIbased when tit is missing. */
	getTargetInteractionType = () => {
		const cd = this.props.campaignDetails;
		const fromCd = cd && cd.targetInteractionType;
		if (fromCd !== undefined && fromCd !== null) {
			const s = String(fromCd).trim();
			if (s) return s;
		}
		if (this.getAttackType() === "Vishing" || this.getAttackType() === "WhatsApp") {
			return this.getIsAIbased() ? "inbound" : "outbound";
		}
		const fetched = this.state.fetchedTargetInteractionType;
		if (fetched !== undefined && fetched !== null && String(fetched).trim() !== "") {
			return String(fetched).trim();
		}
		return "";
	};
	/* Whether a landing page has any say over credential harvesting for this attack
	   vector. Inbound Vishing / AI WhatsApp harvest inside the call or chat and never
	   route through a landing page, so a template without one is perfectly usable --
	   only the vectors that do use a landing page may be blocked on a missing one. */
	usesLandingPage = () => attackVectorUsesLandingPage({
		attackType: this.getAttackType(),
		targetInteractionType: this.getTargetInteractionType(),
		isAIbased: this.getIsAIbased()
	});
	/** Prefer campaign details when boolean is defined; avoid !== null only (undefined was wrongly treated as “use details”). */
	getIsAIbased = () => {
		const cd = this.props.campaignDetails;
		if (cd && cd.isAIbased !== undefined && cd.isAIbased !== null) {
			return !!cd.isAIbased;
		}
		return !!this.state.fetchedIsAIbased;
	};

	componentDidMount() {
		if (this.props.campaignId && this.props.simulationId) {
			this.fetchCampaignContext();
		} else if (this.props.readOnly) {
			this.loadTemplates();
		} else {
			this.loadCategories();
		}
	}

	componentWillUnmount() {
		if (this.categoriesCancel) this.categoriesCancel();
		if (this.templatesCancel) this.templatesCancel();
		if (this.simulationCancel) this.simulationCancel();
		if (this.templatesObserver) { this.templatesObserver.disconnect(); this.templatesObserver = null; }
	}

	getLockedTemplateId = () => {
		const raw = this.props.campaignTemplateId;
		if (raw == null || raw === "") return null;
		const id = parseInt(raw, 10);
		return Number.isNaN(id) ? null : id;
	};

	fetchCampaignContext = () => {
		const { campaignId, simulationId } = this.props;
		if (!campaignId || !simulationId) return;
		if (this.simulationCancel) this.simulationCancel();
		const source = getCancelSource();
		if (source) this.simulationCancel = source.cancel;
		axios.get("/api/simulation/", {
			params: { campaignId, simulationId },
			cancelToken: source ? source.token : undefined
		})
			.then((res) => {
				if (res.data?.simulation?.campaign) {
					const sim = res.data.simulation;
					const camp = sim.campaign;
					const attackType = camp.attack_type?.name || "";
					const simulationType = (camp.simulation_type?.name || "").trim();
					const chRaw =
						pickCredentialHarvestingRaw(camp) ?? pickCredentialHarvestingRaw(sim);
					const credentialHarvesting = coerceCredentialHarvesting(chRaw) ?? false;
					const targetInteractionType = camp.targetInteractionType || sim.targetInteractionType || "";
					const fetchedIsAIbased = !!(camp.isAIbased ?? sim.isAIbased);
					this.setState(
						{
							fetchedAttackType: attackType,
							fetchedSimulationType: simulationType,
							fetchedCredentialHarvesting: credentialHarvesting,
							fetchedTargetInteractionType: targetInteractionType,
							fetchedIsAIbased,
						},
						() => this.loadCategories()
					);
				} else {
					this.loadCategories();
				}
			})
			.catch((err) => {
				if (!axiosLib.isCancel(err)) this.setState({ loading: false });
				this.loadCategories();
			});
	};

	componentDidUpdate(prevProps) {
		// Sync template source when the parent changes it (e.g. navigating back to this step).
		if (prevProps.templateType !== this.props.templateType) {
			const nextType = this.props.templateType === "System Templates" ? "System Templates" : "My Templates";
			if (nextType !== this.state.templateType) {
				this.setState({ templateType: nextType, storeAttackTypeId: null }, () => this.loadCategories());
				return;
			}
		}
		// Load when filter props change
		if (prevProps.simulationType !== this.props.simulationType ||
			prevProps.attackType !== this.props.attackType ||
			prevProps.credentialHarvesting !== this.props.credentialHarvesting ||
			prevProps.campaignDetails?.credentialHarvesting !== this.props.campaignDetails?.credentialHarvesting) {
			if (this.props.readOnly) this.loadTemplates();
			else this.loadCategories();
			return;
		}
		if (prevProps.campaignDetails?.targetInteractionType !== this.props.campaignDetails?.targetInteractionType ||
			prevProps.campaignDetails?.isAIbased !== this.props.campaignDetails?.isAIbased) {
			if (this.props.readOnly) this.loadTemplates();
			else this.loadCategories();
			return;
		}
		// Load when attackType or simulationType becomes available (e.g. async from parent) and we haven't loaded yet
		const filterNowAvailable = (!prevProps.attackType && this.props.attackType) || (!prevProps.simulationType && this.props.simulationType);
		if (filterNowAvailable && this.state.categories.length === 0 && !this.state.loading) {
			if (this.props.readOnly) this.loadTemplates();
			else this.loadCategories();
		}
		const prevCamp = prevProps.campaignId;
		const nextCamp = this.props.campaignId;
		const prevSim = prevProps.simulationId;
		const nextSim = this.props.simulationId;
		if ((prevCamp !== nextCamp || prevSim !== nextSim) && nextCamp && nextSim) {
			this.fetchCampaignContext();
		}

		if (prevProps.templateId !== this.props.templateId) {
			this.setState({ selectedTemplateId: this.props.templateId || null });
		}
		// Sync selection from parent when user navigates back (e.g. from Preview) so step reflects current state
		const prevIds = Array.isArray(prevProps.selectedTemplateIds) ? prevProps.selectedTemplateIds : [];
		const nextIds = Array.isArray(this.props.selectedTemplateIds) ? this.props.selectedTemplateIds : [];
		const idsEqual = prevIds.length === nextIds.length && nextIds.every((id, i) => id === prevIds[i]);
		if (!idsEqual || prevProps.templateId !== this.props.templateId) {
			this.setState({
				selectedTemplateId: this.props.templateId || null,
				selectedTemplateIds: nextIds.length ? [...nextIds] : []
			});
		}
		// Sync category filters when parent updates (e.g. navigate back to this step). Parent keeps full selectedCategoryIds array.
		const prevCatIds = categoryIdsFromProps(prevProps);
		const nextCatIds = categoryIdsFromProps(this.props);
		const categoryPropChanged =
			prevCatIds.length !== nextCatIds.length ||
			nextCatIds.some((id, i) => id !== prevCatIds[i]);
		// Every local toggle round-trips through the parent's onUpdate and comes back down as a
		// "changed" prop even though it's just an echo of what we already hold — e.g. unchecking
		// "All" sends selectedCategoryIds:[], the parent stores it and passes it right back, and
		// without this guard the sync below would fire and force categoryFilterNone back to false,
		// making "All" look like it silently re-checked itself. Only treat it as external syncing
		// when the incoming ids genuinely differ from what we already have locally.
		const categoryPropMatchesLocalState =
			this.state.selectedCategoryIds.length === nextCatIds.length &&
			nextCatIds.every((id, i) => String(id) === String(this.state.selectedCategoryIds[i]));
		const templateModePropChanged = prevProps.templateMode !== this.props.templateMode;
		if ((categoryPropChanged && !categoryPropMatchesLocalState) || templateModePropChanged) {
			if (categoryPropChanged && !categoryPropMatchesLocalState) {
				const ids = nextCatIds;
				const newCategory = ids[0] || "";
				this.setState({
					selectedCategory: newCategory,
					selectedCategoryIds: ids,
					categoryFilterNone: false,
					templateMode: this.props.templateMode || "single",
					selectedTemplateIds: Array.isArray(this.props.selectedTemplateIds) ? this.props.selectedTemplateIds : []
				}, () => this.loadTemplates());
			} else if (templateModePropChanged) {
				this.setState({
					templateMode: this.props.templateMode || "single",
					selectedTemplateIds: Array.isArray(this.props.selectedTemplateIds) ? this.props.selectedTemplateIds : []
				});
			}
		}
	}
	/** Build the request URL + params for a given page of templates. */
	buildTemplateRequest = (pageNum, categoryIdsOverride) => {
		const ids = categoryIdsOverride != null ? categoryIdsOverride : this.state.selectedCategoryIds;
		const search = (this.state.templateSearchInput || "").trim();
		const lockedId = this.getLockedTemplateId();
		// A locked (prefilled) template is always a "My Template"; fetch just that one card.
		const isSystem = lockedId == null && this.isSystemType();
		const url = isSystem ? '/api/store/fetch-store-system-templates' : '/api/campaign/template/template';
		const params = { limit: TEMPLATE_PAGE_SIZE, page: pageNum };
		if (isSystem) {
			params.type = "System Templates";
			// Unlike My Templates, the store endpoint actually filters by this — but credential
			// harvesting is a campaign-level setting now, not a template-level one (My Templates
			// already ignores it), so System should list every template regardless of the toggle too.
			params.targetInteractionType = this.getTargetInteractionType();
			params.isAIbased = this.getIsAIbased();
			if (this.state.storeAttackTypeId != null) params.attackTypeIds = [this.state.storeAttackTypeId];
		} else {
			params.type = "My Templates";
			params.attackType = this.getAttackType() ?? "";
			params.credentialHarvesting = this.getCredentialHarvesting();
			params.simulationType = this.getSimulationType() ?? "";
			params.targetInteractionType = this.getTargetInteractionType();
			params.isAIbased = this.getIsAIbased();
		}
		if (lockedId != null) params.campaignTemplateId = lockedId;
		if (ids && ids.length > 0) params.categoryIds = ids;
		if (search) params.search = search;
		return { url, params, isSystem, lockedId };
	};

	/** Merge appended templates, de-duping by id so overlapping pages never double-render a card. */
	mergeTemplates = (existing, incoming) => {
		const seen = new Set(existing.map((t) => t.id));
		const merged = existing.slice();
		for (const t of incoming) {
			if (!seen.has(t.id)) { seen.add(t.id); merged.push(t); }
		}
		return merged;
	};

	/** Fresh load: resets to page 1 and replaces the list. `categoryIdsOverride` (legacy) still honored. */
	loadTemplates = (categoryIdsOverride, options = {}) => {
		const ids = categoryIdsOverride !== undefined
			? (Array.isArray(categoryIdsOverride) ? categoryIdsOverride : [categoryIdsOverride])
			: null;
		this.fetchTemplatesPage(1, { append: false, categoryIdsOverride: ids, preserveSelection: options.preserveSelection });
	};

	/** Infinite scroll: fetch the next page and append. */
	loadMoreTemplates = () => {
		if (this.state.loading || this.state.loadingMore || !this.state.hasMore) return;
		if (this.getLockedTemplateId() != null) return;
		this.fetchTemplatesPage(this.state.page + 1, { append: true });
	};

	fetchTemplatesPage = (pageNum, { append = false, categoryIdsOverride = null, preserveSelection = false } = {}) => {
		// User explicitly deselected every category ("All" unchecked with nothing else checked) — show
		// zero templates without hitting the API. A locked/prefilled template is unaffected (fetched by id).
		if (this.state.categoryFilterNone && categoryIdsOverride == null && this.getLockedTemplateId() == null) {
			if (this.templatesCancel) {
				this.templatesCancel();
				this.templatesCancel = null;
			}
			this.setState({
				loading: false,
				loadingMore: false,
				templates: append ? this.state.templates : [],
				page: pageNum,
				hasMore: false,
				totalCount: 0,
			}, () => {
				if (!append && !preserveSelection) this.updateData();
			});
			return;
		}

		const { url, params, isSystem, lockedId } = this.buildTemplateRequest(pageNum, categoryIdsOverride);

		if (this.templatesCancel) {
			this.templatesCancel();
			this.templatesCancel = null;
		}
		const source = getCancelSource();
		if (source) this.templatesCancel = source.cancel;
		this.setState(append ? { loadingMore: true } : { loading: true });

		axios.get(url, {
			params,
			cancelToken: source ? source.token : undefined
		})
			.then((res) => {
				let fetched = res.data.campaign_template || res.data.campaign_templates || [];
				const templatePath = res.data.path || "";
				const count = typeof res.data.count === "number" ? res.data.count : null;
				// Store templates only send a thumbnail filename + base path; build the URL the card expects.
				if (isSystem && templatePath) {
					fetched = fetched.map((t) => {
						if (t && t.template && t.template.thumbnail && !t.template.thumbnailUrl) {
							return { ...t, template: { ...t.template, thumbnailUrl: templatePath + t.template.thumbnail } };
						}
						return t;
					});
				}
				this.setState((prev) => {
					const templates = append ? this.mergeTemplates(prev.templates, fetched) : fetched;
					// An empty page always ends the scroll, even if a stale/racy `count` still looks larger
					// than what we can actually load (otherwise loadMore would fire forever).
					const hasMore = lockedId != null
						? false
						: fetched.length === 0
							? false
							: (count != null ? templates.length < count : fetched.length === TEMPLATE_PAGE_SIZE);
					return {
						loading: false,
						loadingMore: false,
						templates,
						templatePath,
						page: pageNum,
						hasMore,
						totalCount: count != null ? count : prev.totalCount,
						...(!append && !preserveSelection && templates.length <= 1 && prev.templateMode === "random"
							? { templateMode: "single", selectedTemplateIds: [] }
							: {})
					};
				}, () => {
					// Refresh parent selectedTemplates after a fresh load so Preview stays in sync.
					if (!append && !preserveSelection) this.updateData();
					// IntersectionObserver only fires on transitions; if the sentinel is still in view after
					// appending (the new page didn't push it past the viewport), re-observe so the callback
					// re-fires and the next page loads until the viewport is filled or there's nothing more.
					if (append && this.state.hasMore && this.templatesObserver && this.sentinelNode) {
						this.templatesObserver.unobserve(this.sentinelNode);
						this.templatesObserver.observe(this.sentinelNode);
					}
				});
			})
			.catch((error) => {
				if (!axiosLib.isCancel(error)) {
					this.setState(append ? { loadingMore: false } : { loading: false, templates: [], hasMore: false });
					apiError(error);
				}
			});
	};

	/** Observe a sentinel at the end of the grid; when it scrolls into view, load the next page. */
	handleSentinelRef = (node) => {
		if (this.templatesObserver) { this.templatesObserver.disconnect(); this.templatesObserver = null; }
		this.sentinelNode = node || null;
		if (!node || typeof IntersectionObserver === "undefined") return;
		const root = node.closest(".campaign-main-content") || null;
		this.templatesObserver = new IntersectionObserver((entries) => {
			if (entries.some((e) => e.isIntersecting)) this.loadMoreTemplates();
		}, { root, rootMargin: "300px" });
		this.templatesObserver.observe(node);
	};

	loadCategories = () => {
		const attackType = this.getAttackType();
		const simulationType = this.getSimulationType();
		if (!attackType && !simulationType) {
			// A prefilled (locked) template is fetched by id and needs no category/attack context, so
			// still load it even when attack/simulation type haven't been resolved.
			if (this.getLockedTemplateId() != null) this.loadTemplates();
			return;
		}

		if (this.isSystemType()) {
			this.loadSystemCategories();
			return;
		}

		if (this.categoriesCancel) {
			this.categoriesCancel();
			this.categoriesCancel = null;
		}
		const source = getCancelSource();
		if (source) this.categoriesCancel = source.cancel;
		this.setState({ loading: true });
		axios.get('/api/campaign/template/categories', {
			params: {
				type: "My Templates",
				attackType: attackType ?? "",
				credentialHarvesting: this.getCredentialHarvesting(),
				simulationType: simulationType ?? "",
				targetInteractionType: this.getTargetInteractionType(),
				isAIbased: this.getIsAIbased()
			},
			cancelToken: source ? source.token : undefined
		})
			.then((res) => {
				const categories = res.data.template_category || [];
				this.setState({ loading: false, categories }, () => {
					const ids = categoryIdsFromProps(this.props);
					this.setState({ selectedCategoryIds: ids, selectedCategory: ids[0] || "", categoryFilterNone: false }, () => this.loadTemplates());
				});
			})
			.catch((error) => {
				if (!axiosLib.isCancel(error)) {
					this.setState({ loading: false, categories: [] });
					apiError(error);
				}
			});
	};

	/** System (store) templates: resolve the chosen attack-type id, then load store categories + templates. */
	loadSystemCategories = () => {
		if (this.categoriesCancel) {
			this.categoriesCancel();
			this.categoriesCancel = null;
		}
		const source = getCancelSource();
		if (source) this.categoriesCancel = source.cancel;
		this.setState({ loading: true });
		this.resolveStoreAttackTypeId()
			.then(() => axios.get('/api/store/fetch-categories', { cancelToken: source ? source.token : undefined }))
			.then((res) => {
				const raw = res.data.response || [];
				// Normalize to the shape the category dropdown renders ({ category: { id, name } }).
				const categories = raw.map((c) => ({ category: { id: c.id, name: c.name } }));
				this.setState({ loading: false, categories }, () => {
					const ids = categoryIdsFromProps(this.props);
					this.setState({ selectedCategoryIds: ids, selectedCategory: ids[0] || "", categoryFilterNone: false }, () => this.loadTemplates());
				});
			})
			.catch((error) => {
				if (!axiosLib.isCancel(error)) {
					this.setState({ loading: false, categories: [] });
					apiError(error);
				}
			});
	};

	/** Map the chosen attack-type name to its id so store templates can be filtered by attack vector. */
	resolveStoreAttackTypeId = () => {
		const attackType = this.getAttackType();
		if (!attackType) return Promise.resolve(null);
		return axios.get('/api/attacktypes', {})
			.then((res) => {
				const list = res.data.attackTypes || [];
				const match = list.find((a) => a.name === attackType);
				const id = match ? match.id : null;
				this.setState({ storeAttackTypeId: id });
				return id;
			})
			.catch(() => {
				this.setState({ storeAttackTypeId: null });
				return null;
			});
	};



	handleTemplateTypeChange = (value) => {
		const templateType = value === "System Templates" ? "System Templates" : "My Templates";
		if (templateType === this.state.templateType) return;
		// Switching source invalidates any current category/template selection.
		this.setState({
			templateType,
			categories: [],
			selectedCategory: "",
			selectedCategoryIds: [],
			categoryFilterNone: false,
			selectedTemplateId: null,
			selectedTemplateIds: [],
			templateMode: "single",
			storeAttackTypeId: null,
			templateSearchInput: ""
		}, () => {
			this.updateData();
			this.loadCategories();
		});
	};

	handleCategoryChange = (e) => {
		const value = e.target.value;
		const ids = value === "" ? [] : [value];
		this.setState({
			selectedCategory: value,
			selectedCategoryIds: ids,
			categoryFilterNone: false,
			selectedTemplateId: null,
			selectedTemplateIds: []
		}, () => {
			this.loadTemplates();
			this.updateData();
		});
	};

	/** All category ids currently available in the filter list. */
	getAllCategoryIds = () => (this.state.categories || []).map((cat) => cat.category?.id || cat.id);

	/** True when every category checkbox currently reads as checked (implicit-all "no filter", or every id explicitly selected). */
	isEveryCategorySelected = () => {
		if (this.state.categoryFilterNone) return false;
		if (this.state.selectedCategoryIds.length === 0) return true;
		const allIds = this.getAllCategoryIds();
		// Compare as strings: category ids from the API (numbers) and ids stored from legacy
		// single-select props (strings) shouldn't silently fail `.includes` on a type mismatch.
		const selected = this.state.selectedCategoryIds.map(String);
		return allIds.length > 0 && allIds.every((id) => selected.includes(String(id)));
	};

	handleCategoryAllToggle = () => {
		const wasAllSelected = this.isEveryCategorySelected();
		// Toggling off "All" means the user explicitly wants zero categories (show nothing);
		// toggling it on selects every id explicitly so each item reads as checked.
		const newIds = wasAllSelected ? [] : this.getAllCategoryIds();
		this.setState({
			selectedCategoryIds: newIds,
			selectedCategory: newIds[0] || "",
			categoryFilterNone: wasAllSelected,
			selectedTemplateId: null,
			selectedTemplateIds: []
		}, () => {
			this.loadTemplates();
			this.updateData();
		});
	};

	handleCategoryToggle = (catId) => {
		// Implicit-all state ([] with categoryFilterNone false) expands to the explicit full list first,
		// so unchecking one item keeps the rest checked instead of collapsing to "only this one selected".
		const ids = (this.state.selectedCategoryIds.length === 0 && !this.state.categoryFilterNone)
			? this.getAllCategoryIds()
			: this.state.selectedCategoryIds;
		const isSelected = ids.some((id) => String(id) === String(catId));
		const newIds = isSelected ? ids.filter((id) => String(id) !== String(catId)) : [...ids, catId];
		this.setState({
			selectedCategoryIds: newIds,
			selectedCategory: newIds[0] || "",
			// Unchecking the last remaining category means "explicitly none"; checking any means it's no longer "none".
			categoryFilterNone: newIds.length === 0,
			selectedTemplateId: null,
			selectedTemplateIds: []
		}, () => {
			this.loadTemplates();
			this.updateData();
		});
	};

	handleTemplateModeChange = (mode) => {
		const canUseMultiple = (this.state.templates || []).length > 1;
		if (mode === "random" && !canUseMultiple) return;
		this.setState({
			templateMode: mode,
			selectedTemplateId: null,
			selectedTemplateIds: []
		}, () => this.updateData());
	};

	handleTemplateSelect = (templateId) => {
		if (this.state.templateMode === "single") {
			this.setState({ selectedTemplateId: templateId }, () => this.updateData());
		} else {
			const currentIds = this.state.selectedTemplateIds || [];
			const isSelected = currentIds.includes(templateId);
			const newIds = isSelected
				? currentIds.filter(id => id !== templateId)
				: [...currentIds, templateId];
			this.setState({ selectedTemplateIds: newIds }, () => this.updateData());
		}
	};

	handleTemplateSearchChange = (e) => {
		const value = e.target.value;
		this.setState({ templateSearchInput: value }, () => {
			this.loadTemplates(undefined, { preserveSelection: true });
		});
	};

	updateData = () => {
		if (!this.props.onUpdate) return;
		let { templateMode, selectedTemplateId, selectedTemplateIds, templates, templatePath } = this.state;

		// After filters/categories reload, drop ids that are no longer in the list so preview/parent stay
		// in sync. Only safe once every page is loaded (!hasMore): with infinite scroll a selected id may
		// simply live on a not-yet-loaded page (e.g. after the step remounts on step navigation), and
		// must NOT be pruned — doing so would silently drop multi-page random selections.
		if (templates.length > 0 && !this.state.hasMore) {
			if (templateMode === "single" && selectedTemplateId != null && !templates.some((tpl) => tpl.id === selectedTemplateId)) {
				this.setState({ selectedTemplateId: null }, () => this.updateData());
				return;
			}
			if (templateMode === "random" && selectedTemplateIds.length > 0) {
				const nextIds = selectedTemplateIds.filter((id) => templates.some((tpl) => tpl.id === id));
				if (nextIds.length !== selectedTemplateIds.length) {
					this.setState({ selectedTemplateIds: nextIds }, () => this.updateData());
					return;
				}
			}
		}

		const idsForParent =
			templateMode === "single"
				? selectedTemplateId != null && selectedTemplateId !== ""
					? [selectedTemplateId]
					: []
				: selectedTemplateIds;

		const templateId =
			templateMode === "single"
				? selectedTemplateId
				: selectedTemplateIds.length > 0
					? selectedTemplateIds[0]
					: null;

		let selectedTemplates = [];
		if (templateMode === "single" && selectedTemplateId) {
			const t = templates.find((tpl) => tpl.id === selectedTemplateId);
			if (t) {
				selectedTemplates = [
					{
						id: t.id,
						name: t.name,
						thumbnail: t.template?.thumbnail,
						thumbnailUrl: t.template?.thumbnailUrl,
						templatePath: templatePath,
						/* null = this template has no landing page; undefined = not reported
						   by this endpoint (system templates), so don't claim either way */
						landingUrl: readLandingUrl(t)
					}
				];
			}
		} else if (templateMode === "random" && selectedTemplateIds.length > 0) {
			selectedTemplates = selectedTemplateIds.map((id) => {
				const t = templates.find((tpl) => tpl.id === id);
				return t
					? {
							id: t.id,
							name: t.name,
							thumbnail: t.template?.thumbnail,
							thumbnailUrl: t.template?.thumbnailUrl,
							templatePath: templatePath,
							landingUrl: readLandingUrl(t)
						}
					: { id, name: "Template" };
			});
		}

		this.props.onUpdate({
			templateId,
			templateType: this.state.templateType,
			selectedCategory: this.state.selectedCategory,
			selectedCategoryIds: this.state.selectedCategoryIds,
			selectedTemplateIds: idsForParent,
			templateMode,
			selectedTemplates
		});
	};

	formatDate = (dateString) => {
		if (!dateString) return "";
		return getDate(dateString);
	};

	validate = () => {
		const canUseMultiple = (this.state.templates || []).length > 1;
		if (this.state.templateMode === "single") {
			const hasSelection = this.state.selectedTemplateId != null && this.state.selectedTemplateId !== "";
			if (!hasSelection) {
				toast.error("Please select a template");
				return false;
			}
		} else {
			if (!canUseMultiple) {
				toast.error("Multiple mode is available only when more than one template is available");
				return false;
			}
			if (!this.state.selectedTemplateIds || this.state.selectedTemplateIds.length < 2) {
				toast.error("Please select at least two templates");
				return false;
			}
		}
		return true;
	};

	/* For the vectors that route through a landing page, harvesting needs one to harvest
	   on: a template without it sends the target straight to the awareness page whatever
	   this campaign's toggle says -- the backend enforces that -- so say so here instead
	   of letting the Preview claim harvesting is on. Vectors that never use a landing
	   page (inbound Vishing / AI WhatsApp) are exempt; see usesLandingPage. */
	renderNoLandingPageNotice = () => {
		if (!this.getCredentialHarvesting() || !this.usesLandingPage()) return null;
		const selected = this.state.templates.filter((t) =>
			(this.state.selectedTemplateIds || []).includes(t.id) || t.id === this.state.templateId
		);
		if (!selected.length) return null;
		/* undefined = endpoint didn't report a page (system templates); don't guess */
		const known = selected.filter((t) => readLandingUrl(t) !== undefined);
		if (!known.length) return null;
		const without = known.filter((t) => !readLandingUrl(t));
		if (!without.length) return null;
		const all = without.length === known.length;
		return (
			<div className="template-no-landing-notice">
				{all
					? "This template has no landing page, so credential harvesting will not apply — targets who click the link go straight to the awareness page."
					: `${without.length} of the selected templates have no landing page. For those, targets who click the link go straight to the awareness page.`}
			</div>
		);
	};

	render() {
		const { categories, selectedCategoryIds, categoryFilterNone, templates, loading, templateMode, selectedTemplateId, selectedTemplateIds, templateSearchInput } = this.state;
		const { readOnly } = this.props;
		const canUseMultiple = (templates || []).length > 1;
		const categoryLabel = categoryFilterNone
			? "None"
			: this.isEveryCategorySelected()
				? "All"
				: String(selectedCategoryIds.length);

		return (
			<div className="step-content-wrapper">
				<div className="step-header">
					<h4>Template Selection</h4>
					<p>Select the attack template you want to use for this simulation.</p>
				</div>
				{
					!readOnly && (
						<div className={`template-selection-filters ${readOnly ? "read-only" : ""}`} style={readOnly ? { pointerEvents: "none", opacity: 0.9 } : {}}>
					<div className="filter-row">
						<Form.Group className="filter-group">
							<Form.Label className="filter-label">Type</Form.Label>
							<Dropdown className="template-type-filter-dropdown">
								<Dropdown.Toggle variant="secondary" id="template-type-filter-dropdown" className="filter-select template-type-dropdown-toggle">
									{this.state.templateType === "System Templates" ? "System" : "My Templates"}
								</Dropdown.Toggle>
								<Dropdown.Menu>
									<Dropdown.Item
										active={this.state.templateType === "System Templates"}
										onClick={() => this.handleTemplateTypeChange("System Templates")}
									>
										System
									</Dropdown.Item>
									<Dropdown.Item
										active={this.state.templateType === "My Templates"}
										onClick={() => this.handleTemplateTypeChange("My Templates")}
									>
										My Templates
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</Form.Group>
						<div className="filter-separator" />
						<Form.Group className="filter-group">
							<Form.Label className="filter-label">Filter By</Form.Label>
							<Dropdown className="category-filter-dropdown">
								<Dropdown.Toggle variant="secondary" id="category-filter-dropdown" className="filter-select category-dropdown-toggle">
									Category: {categoryLabel}
								</Dropdown.Toggle>
								<Dropdown.Menu className="category-multiselect-menu">
									<Dropdown.Item
										as="div"
										className="category-filter-item"
										onClick={(e) => { e.preventDefault(); this.handleCategoryAllToggle(); }}
									>
										{/* No `label` prop on Form.Check below — a real <label for> associated with the
										    input natively forwards a second click to it, which was causing one user
										    click to register as two toggles that canceled out. Rendering the text as a
										    plain <span> (not a <label>) removes that mechanism entirely, so this
										    wrapper's onClick is the only thing that can ever fire the toggle. */}
										<div
											className="category-filter-checkbox-wrap"
											onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.handleCategoryAllToggle(); }}
										>
											<Form.Check
												type="checkbox"
												id="category-filter-all"
												checked={this.isEveryCategorySelected()}
												onChange={() => {}}
												readOnly
											/>
											<span className="form-check-label">All</span>
										</div>
									</Dropdown.Item>
									{categories.map((cat, index) => {
										const catId = cat.category?.id || cat.id;
										const catName = cat.category?.name || cat.name || "";
										const label = cat.count != null ? `${catName} (${cat.count} Templates)` : catName;
										const isChecked = !categoryFilterNone && (
											selectedCategoryIds.length === 0 ||
											selectedCategoryIds.some((id) => String(id) === String(catId))
										);
										return (
											<Dropdown.Item
												key={catId}
												as="div"
												className="category-filter-item"
												onClick={(e) => { e.preventDefault(); this.handleCategoryToggle(catId); }}
											>
												<div
													className="category-filter-checkbox-wrap"
													onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.handleCategoryToggle(catId); }}
												>
													<Form.Check
														type="checkbox"
														id={`category-filter-${catId}`}
														checked={isChecked}
														onChange={() => {}}
														readOnly
													/>
													<span className="form-check-label">{label}</span>
												</div>
											</Dropdown.Item>
										);
									})}
								</Dropdown.Menu>
							</Dropdown>
						</Form.Group>
						<div className="filter-separator" />
						<Form.Group className="filter-group">
							<Form.Label className="template-mode-label">Template Mode</Form.Label>
							<div className="template-mode-options">
								<div
									className={`template-mode-option ${templateMode === "single" ? "selected" : ""}`}
									onClick={() => this.handleTemplateModeChange("single")}
								>
									<Form.Check
										type="radio"
										name="templateMode"
										onChange={() => this.handleTemplateModeChange("single")}
										label={
											<span className="template-mode-label-wrap">
												Single
												<OverlayTrigger
													placement="top"
													overlay={<Tooltip id="single-template-mode-tip">Uses one template for all users in the campaign.</Tooltip>}
												>
													<span className="template-mode-info-icon" onClick={(e) => e.stopPropagation()}>
														<PiInfo />
													</span>
												</OverlayTrigger>
											</span>
										}
										checked={templateMode === "single"}
									/>
								</div>
								<div className="filter-separator-in" />
								<div
									className={`template-mode-option ${templateMode === "random" ? "selected" : ""} ${!canUseMultiple ? "disabled" : ""}`}
									onClick={() => canUseMultiple && this.handleTemplateModeChange("random")}
								>
									<Form.Check
										type="radio"
										name="templateMode"
										onChange={() => this.handleTemplateModeChange("random")}
										label={
											<span className="template-mode-label-wrap">
												Random
												<OverlayTrigger
													placement="top"
													overlay={<Tooltip id="multiple-template-mode-tip">Distributes multiple selected templates randomly among users.</Tooltip>}
												>
													<span className="template-mode-info-icon" onClick={(e) => e.stopPropagation()}>
														<PiInfo />
													</span>
												</OverlayTrigger>
											</span>
										}
										checked={templateMode === "random"}
										disabled={!canUseMultiple}
									/>
								</div>
							</div>
						</Form.Group>
						<Form.Group className="template-search-group">
							<Form.Control
								type="search"
								className="template-search-input"
								placeholder="Search"
								value={templateSearchInput}
								onChange={this.handleTemplateSearchChange}
								aria-label="Search templates"
							/>
						</Form.Group>
					</div>
				</div>

					)
				}
				
				{loading ? (
					<div className="template-loading">Loading templates...</div>
				) : (
					<div className="template-grid">
						{templates.length > 0 ? (
							templates.map((template, index) => {
								const isSelected = templateMode === "single"
									? selectedTemplateId === template.id
									: selectedTemplateIds.includes(template.id);

								const thumbnailSrc = template.template?.thumbnailUrl
									? template.template.thumbnailUrl
									: simulationandtrain;

								/* Where harvesting does happen on a landing page, a template without one can't
								   be used for this campaign, so block selecting it instead of letting the user
								   pick it and only warning afterwards (see renderNoLandingPageNotice for the
								   case where CH is turned on after a page-less template was selected). The
								   vectors that harvest in the call/chat itself never hit this — usesLandingPage. */
								const templateLandingUrl = readLandingUrl(template);
								const noLandingPage = this.getCredentialHarvesting() && this.usesLandingPage() &&
									templateLandingUrl !== undefined && !templateLandingUrl;
								const isDisabled = readOnly || noLandingPage;

								const card = (
									<div
										key={index}
										className={`template-card ${isSelected ? "selected" : ""} ${isDisabled ? "read-only" : ""}`}
										onClick={() => !isDisabled && this.handleTemplateSelect(template.id)}
										 style={{
											filter: isDisabled ? "grayscale(70%)" : "none",
											// Only readOnly (a preview with no interactions at all) goes pointer-events:none —
											// a no-landing-page card must stay hoverable so its tooltip can still show.
											pointerEvents: readOnly ? "none" : "auto",
											opacity: isDisabled ? 0.85 : 1,
											cursor: noLandingPage ? "not-allowed" : undefined,
										}}
									>
										<div className="template-card-image">
											<img
												src={thumbnailSrc}
												alt={template.name || "Template thumbnail"}
												className="img-fluid"
											/>
											{/* {template.simulation_type && (
												<div className="template-simulation-badge">
													{template.simulation_type.name}
												</div>
											)} */}
											{templateMode === "random" && (
												<div className="template-card-checkbox-wrapper">
													<Form.Check
														type="checkbox"
														checked={isSelected}
														disabled={isDisabled}
														onChange={(e) => {
															e.stopPropagation();
															if (!isDisabled) this.handleTemplateSelect(template.id);
														}}
														onClick={(e) => e.stopPropagation()}
														className="template-card-checkbox-input"
													/>
												</div>
											)}
										</div>
										<div className="template-card-content">
											<div className="template-card-title-row">
												<h6 className="template-card-title">{template.name}</h6>
												<PiEye
													className="template-view-icon"
													onClick={(e) => {
														e.stopPropagation();
														if (this.props.onPreviewTemplate) this.props.onPreviewTemplate(template.id);
													}}
												/>
											</div>
											<p className="template-card-date">{this.formatDate(template.createdAt)}</p>
											<div className="template-card-tags">
												{template.attack_type && (
													<span className="template-tag-attack">{template.attack_type.name}</span>
												)}
												{/* {template.simulation_type && (
													<span className="template-tag-category">{template.simulation_type.name}</span>
												)} */}
											</div>
										</div>
									</div>
								);

								if (!noLandingPage) return card;

								// Attach directly to the card div (no extra wrapper) — a wrapping
								// span here would sit in the grid as its own item and, unlike every
								// other card, size to its content instead of the grid track's width,
								// which is what was making these cards visibly resize on hover.
								return (
									<OverlayTrigger
										key={index}
										placement="top"
										overlay={<Tooltip id={`no-landing-page-${template.id}`}>No landing page</Tooltip>}
									>
										{card}
									</OverlayTrigger>
								);
							})
						) : (
							templateSearchInput.trim() ? (
								<div className="template-empty">No templates found</div>
							) : selectedCategoryIds.length > 0 ? (
								<div className="template-empty">No templates found in this category.</div>
							) : (
								<div className="template-empty">Please select a category to view templates.</div>
							)
						)}
						{templates.length > 0 && this.state.hasMore && !readOnly && this.getLockedTemplateId() == null && (
							<div ref={this.handleSentinelRef} className="template-scroll-sentinel" style={{ gridColumn: "1 / -1", height: 1 }} />
						)}
						{this.state.loadingMore && (
							<div className="template-loading" style={{ gridColumn: "1 / -1" }}>Loading more...</div>
						)}
					</div>
				)}
				{this.renderNoLandingPageNotice()}
			</div>
		);
	}
}
